import { apiConfig } from '@/config/api';
import { authenticatedFetch, APIError } from '@/lib/api/authenticatedFetch';

// ============================================================================
// Type Definitions (based on OpenAPI 3.1.0 spec)
// ============================================================================

export interface VINSamplingOptions {
  strategy?: 'all' | 'random' | 'first' | 'last';
  count?: number;
}

export interface CreateJobRequest {
  // Entry Point 1: Inventory date + make_name
  inventory_date?: string;
  zone_slug?: string;
  make_name?: string;

  // Entry Point 2: Step 1 execution run_id
  run_id?: string;

  // Entry Point 3: Direct Apify run ID
  apify_run_id?: string;

  // Direct VIN list (alternative to entry points)
  vins?: string[];

  // Common parameters
  tenant_id?: string;
  tenant_slug?: string;
  oem?: string;
  auto_trigger?: boolean;
  force_rerun?: boolean;

  // Sampling options
  sampling?: VINSamplingOptions;
  limit?: number; // Deprecated - use sampling.count instead

  // Metadata
  metadata?: {
    requested_by?: string;
    requested_at?: string;
    [key: string]: unknown;
  };
}

export interface CreateJobResponse {
  status: string;
  message: string;
  request_id?: string; // OpenAPI spec field (UUID) - unique request identifier for idempotency
  job_id?: string; // Job ID returned from dispatcher (only present when using direct HTTP dispatch)
  dispatch_method?: 'direct_http' | 'cloud_tasks'; // Method used to dispatch the job request
  task_name?: string | null; // Cloud Tasks task name (only present when dispatch_method is 'cloud_tasks')
  queue?: string | null; // Cloud Tasks queue name (only present when dispatch_method is 'cloud_tasks')
  sampling_applied?: {
    strategy: string;
    count: number;
  } | null;
}

export interface JobStatus {
  job_id: string;
  status: 'pending' | 'running' | 'completed' | 'partial_success' | 'failed' | 'cancelled';
  total_vins: number;
  processed_vins: number;
  failed_vins: number;
  progress_percent?: number;
  vins_with_recalls?: number;
  total_recalls?: number; // Total number of recalls found (from vin_recall_details table)
  recalls_with_advance_notice?: number; // Count of recalls with advance_notice = 'Y'
  processed_vins_list?: string[]; // Array of successfully processed VINs (no errors)
  failed_vins_list?: string[]; // Array of failed VINs (from vin_processing_results and vin_processing_errors)
  started_at?: string | null;
  completed_at?: string | null;
  entry_point?: string | null;
  input_params?: Record<string, unknown> | null;
  sampling?: {
    strategy: string;
    count: number;
  } | null;
  requested_by?: string | null;
  requested_at?: string | null;
  output_summary?: Record<string, unknown> | null;
  errors?: Array<Record<string, unknown>> | null;
  cloud_run?: {
    execution_name?: string;
    execution_status?: {
      condition_state?: 'CONDITION_SUCCEEDED' | 'CONDITION_FAILED' | 'CONDITION_RECONCILING';
      reconciling?: boolean;
      succeeded_count?: number;
      failed_count?: number;
      running_count?: number;
      task_count?: number;
      completion_time?: string | null;
      start_time?: string | null;
      message?: string | null;
      error?: string | null;
      is_cancelled?: boolean;
      last_checked?: string;
    };
  } | null;
}

export interface ExecutionHistory {
  id: string;
  job_id: string;
  tenant_id: string;
  entry_point: string;
  input_params: Record<string, unknown>;
  sampling_strategy?: string | null;
  sampling_count?: number | null;
  status: string;
  total_vins: number;
  processed_vins: number;
  failed_vins: number;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  requested_by?: string | null;
  requested_at?: string;
  output_summary?: Record<string, unknown> | null;
  errors?: Array<Record<string, unknown>> | null;
  // Enhanced metrics from backend (OpenAPI 2.0.0)
  processed_vins_list?: string[] | null; // Array of successfully processed VINs (for <= 1000 VINs)
  processed_vins_list_gcs?: string | null; // GCS path to processed VINs list file (for > 1000 VINs)
  failed_vins_list?: string[] | null; // Array of failed VINs (for <= 1000 VINs)
  failed_vins_list_gcs?: string | null; // GCS path to failed VINs list file (for > 1000 VINs)
  total_recalls?: number | null; // Total count of all recalls (from vin_recall_details table)
  recalls_with_advance_notice?: number | null; // Count of recalls with advance_notice = 'Y'
}

export interface ExecutionHistoryResponse {
  executions: ExecutionHistory[];
  count: number;
}

export interface JobLogEvent {
  id: number;
  log_id: string;
  event_type: string;
  message: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
}

export interface JobErrorLog {
  id: string;
  error_type: string;
  error_message: string;
  context?: Record<string, unknown>;
  job_id: string;
  tenant_id?: string | null;
  vin?: string | null;
  occurred_at: string;
  gcs_path?: string | null;
}

export interface JobLogs {
  job_id: string;
  execution_history: {
    id: string;
    entry_point: string;
    input_params: Record<string, unknown>;
    sampling?: {
      strategy: string;
      count: number;
    } | null;
    requested_by?: string | null;
    requested_at: string;
    started_at?: string | null;
    finished_at?: string | null;
    duration_seconds?: number | null;
  };
  job_status: {
    status: string;
    total_vins: number;
    processed_vins: number;
    failed_vins: number;
    started_at?: string | null;
    completed_at?: string | null;
  };
  configuration: {
    oem: string;
    schema: string;
    batch_write_size?: number | null;
    max_workers?: number | null;
    cloud_run?: {
      job_name: string;
      execution_name: string;
      region: string;
      project_id: string;
      task_index?: number | null;
      task_count?: number | null;
      task_attempt?: number | null;
    } | null;
  };
  storage_locations: {
    vin_list_path?: string | null;
    results_gcs_path?: string | null;
    errors_gcs_path?: string | null;
    user_log_gcs_path?: string | null;
    summary_gcs_path?: string | null;
  };
  events: JobLogEvent[];
  error_logs: JobErrorLog[];
  output_summary?: Record<string, unknown> | null;
  errors?: Array<Record<string, unknown>> | null;
  metadata?: Record<string, unknown>;
  // Enhanced sections from /jobs/{job_id}/logs endpoint (OpenAPI 2.0.0)
  vin_lists?: {
    processed_vins?: string[]; // Array of successfully processed VINs (no errors)
    failed_vins?: string[]; // Array of failed VINs (from vin_processing_results and vin_processing_errors)
  } | null;
  recall_statistics?: {
    total_recalls?: number; // Total count of all recalls from vin_recall_details table
    recalls_with_advance_notice?: number; // Count of recalls with advance_notice = 'Y' from vin_recall_details table
  } | null;
}

export interface ProcessVinsDirectRequest {
  vins: string[];
  job_id?: string;
  oem?: string;
  metadata?: Record<string, unknown>;
}

export interface ProcessVinsDirectResponse {
  job_id: string;
  status: string;
  total_vins: number;
  processed_vins: number;
  failed_vins: number;
  total_recalls: number;
  gcs_path?: string | null;
  results?: Array<Record<string, unknown>>;
}

export interface ServiceInfo {
  service: string;
  version: string;
  endpoints: {
    health: string;
    create_job: string;
    get_job_status: string;
    get_jobs_history: string;
    get_job_logs: string;
    process_direct: string;
  };
}

export interface HealthStatus {
  status: string;
  timestamp: string;
  supabase?: string;
  error?: string;
}

export interface ErrorResponse {
  error: string;
  message?: string | null;
}

export interface GetJobsHistoryParams {
  limit?: number;
  tenant_id?: string;
  entry_point?: 'inventory_date_make' | 'step1_execution' | 'apify_run_id';
  status?: 'running' | 'completed' | 'partial_success' | 'failed' | 'cancelled';
}

// ============================================================================
// API Client Class
// ============================================================================

class VINProcessorAPI {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = apiConfig.vinProcessor.baseUrl;
    this.timeout = apiConfig.vinProcessor.timeout;

    // Debug logging for production troubleshooting
    console.log('[VINProcessorAPI] Initialized with:', {
      baseUrl: this.baseUrl || '(not configured)',
      timeout: this.timeout,
      envValue: import.meta.env.VITE_VIN_PROCESSOR_API_URL || '(not set)'
    });
  }

  private validateBaseUrl(): void {
    if (!this.baseUrl) {
      console.error(
        '[VINProcessorAPI] baseUrl is empty. Check VITE_VIN_PROCESSOR_API_URL env var.'
      );
      throw new APIError('VIN Processor API URL not configured', 500, 'Configuration Error');
    }
  }

  /**
   * Get service information (GET /)
   */
  async getServiceInfo(): Promise<ServiceInfo> {
    this.validateBaseUrl();

    const response = await authenticatedFetch(`${this.baseUrl}/`, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Health check endpoint (GET /health)
   */
  async getHealth(): Promise<HealthStatus> {
    this.validateBaseUrl();

    const response = await authenticatedFetch(`${this.baseUrl}/health`, {
      method: 'GET',
      timeout: this.timeout
    });

    return response.json();
  }

  /**
   * Create a new VIN processing job (POST /jobs/create)
   * Creates a VIN processing job request and dispatches it to the VIN Job Dispatcher service.
   *
   * Default behavior: Direct HTTP dispatch (no Cloud Tasks queue required)
   * - Job is created immediately and appears in execution history
   * - Response includes `job_id` and `dispatch_method: 'direct_http'`
   * - Use `job_id` to track job status immediately via `/jobs/{job_id}/status`
   *
   * Optional behavior: Cloud Tasks queue (only if USE_CLOUD_TASKS=true is set)
   * - Job is queued via Cloud Tasks
   * - Response includes `task_name`, `queue`, and `dispatch_method: 'cloud_tasks'`
   * - Poll `/jobs/history` to find the job after it's processed from the queue
   */
  // eslint-disable-next-line max-lines-per-function, complexity
  async createJob(request: CreateJobRequest): Promise<CreateJobResponse> {
    this.validateBaseUrl();

    const url = `${this.baseUrl}/jobs/create`;

    // Request summary for debugging (no full body)
    const entryPoint = request.run_id
      ? 'step1_execution'
      : request.apify_run_id
        ? 'apify_run_id'
        : request.vins?.length
          ? 'direct_vins'
          : request.inventory_date && request.make_name
            ? 'inventory_date_make'
            : 'unknown';
    console.log('[VINProcessorAPI] createJob request summary:', {
      entryPoint,
      tenant_id: request.tenant_id ?? '(none)',
      tenant_slug: request.tenant_slug ?? '(none)',
      ...(request.run_id && { run_id: request.run_id }),
      ...(request.inventory_date && {
        inventory_date: request.inventory_date,
        make_name: request.make_name
      }),
      ...(request.apify_run_id && { apify_run_id: request.apify_run_id }),
      ...(request.vins && { vins_count: request.vins.length }),
      sampling: request.sampling ?? '(none)'
    });

    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(request),
        timeout: this.timeout
      });

      if (response.status === 202) {
        const data = await response.json();

        console.log('[VINProcessorAPI] createJob success:', {
          status: response.status,
          job_id: data.job_id ?? data.request_id ?? '(none)',
          dispatch_method: data.dispatch_method ?? '(none)'
        });
        if (data.dispatch_method === 'direct_http') {
        } else if (data.dispatch_method === 'cloud_tasks') {
        }
        return data;
      }

      // Handle error responses
      if (!response.ok) {
        let errorData: ErrorResponse;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If JSON parsing fails, try to get text response
          const textResponse = await response.text();

          errorData = { error: textResponse || response.statusText || 'Unknown error' };
        }

        // Extract error message - handle nested error structures
        let errorMessage = errorData.error || 'Failed to create job';
        if (typeof errorData === 'object' && errorData !== null) {
          // Check if error is nested in a message field
          if ('message' in errorData && typeof (errorData as any).message === 'string') {
            errorMessage = (errorData as any).message;
          }
          // Check if there's a detailed error in the response
          if ('detail' in errorData && typeof (errorData as any).detail === 'string') {
            errorMessage = (errorData as any).detail;
          }
        }

        throw new APIError(errorMessage, response.status, response.statusText, errorData);
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('[VINProcessorAPI] createJob failed:', error);
      if (error instanceof APIError && error.response !== undefined) {
        console.error('[VINProcessorAPI] API response body:', error.response);
      }
      if (error instanceof APIError) {
        throw error;
      }
      // Re-throw as APIError if not already
      throw new APIError(
        error instanceof Error ? error.message : 'Unknown error creating job',
        0,
        'Network Error',
        error
      );
    }
  }

  /**
   * Get job status and progress (GET /jobs/{job_id}/status)
   */
  async getJobStatus(jobId: string): Promise<JobStatus> {
    this.validateBaseUrl();

    const response = await authenticatedFetch(`${this.baseUrl}/jobs/${jobId}/status`, {
      method: 'GET',
      timeout: this.timeout
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new APIError(
        errorData.error || 'Failed to get job status',
        response.status,
        response.statusText,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Get execution history for VIN processing jobs (GET /jobs/history)
   */
  async getJobsHistory(params?: GetJobsHistoryParams): Promise<ExecutionHistoryResponse> {
    this.validateBaseUrl();

    const searchParams = new URLSearchParams();
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.tenant_id) {
      searchParams.append('tenant_id', params.tenant_id);
    }
    if (params?.entry_point) {
      searchParams.append('entry_point', params.entry_point);
    }
    if (params?.status) {
      searchParams.append('status', params.status);
    }
    if (params?.date_from) {
      searchParams.append('date_from', params.date_from);
    }

    const queryString = searchParams.toString();
    const url = `${this.baseUrl}/jobs/history${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      timeout: this.timeout
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new APIError(
        errorData.error || 'Failed to get jobs history',
        response.status,
        response.statusText,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Get detailed execution logs for a specific job (GET /jobs/{job_id}/logs)
   * Returns comprehensive verbose logging including execution history, event logs,
   * storage locations, configuration, error logs, and output summaries.
   */
  async getJobLogs(jobId: string): Promise<JobLogs> {
    this.validateBaseUrl();

    const response = await authenticatedFetch(`${this.baseUrl}/jobs/${jobId}/logs`, {
      method: 'GET',
      timeout: this.timeout
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new APIError(
        errorData.error || 'Failed to get job logs',
        response.status,
        response.statusText,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Process VINs directly (synchronous, for testing/small batches) (POST /process)
   * Processes VINs synchronously without creating a Cloud Run Job.
   * Useful for testing or processing small batches (< 100 VINs).
   */
  async processVinsDirect(request: ProcessVinsDirectRequest): Promise<ProcessVinsDirectResponse> {
    this.validateBaseUrl();

    const response = await authenticatedFetch(`${this.baseUrl}/process`, {
      method: 'POST',
      body: JSON.stringify(request),
      timeout: this.timeout
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new APIError(
        errorData.error || 'Failed to process VINs',
        response.status,
        response.statusText,
        errorData
      );
    }

    return response.json();
  }

  /**
   * Refresh Cloud Run execution status for a job (POST /jobs/{job_id}/refresh-cloud-run-status)
   * Manually refreshes Cloud Run execution status for a job.
   * Useful when a job appears stuck in 'running' status.
   */
  async refreshCloudRunStatus(jobId: string): Promise<{
    status: string;
    message: string;
    job_id: string;
    execution_name?: string;
    cloud_run_status?: {
      condition_state: 'CONDITION_SUCCEEDED' | 'CONDITION_FAILED' | 'CONDITION_RECONCILING';
      is_complete: boolean;
      final_status?: 'succeeded' | 'failed' | 'cancelled' | null;
      succeeded_count: number;
      failed_count: number;
      running_count: number;
      task_count: number;
      is_cancelled?: boolean;
    };
  }> {
    this.validateBaseUrl();

    const response = await authenticatedFetch(
      `${this.baseUrl}/jobs/${jobId}/refresh-cloud-run-status`,
      {
        method: 'POST',
        timeout: this.timeout
      }
    );

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new APIError(
        errorData.error || 'Failed to refresh Cloud Run status',
        response.status,
        response.statusText,
        errorData
      );
    }

    return response.json();
  }
}

export const vinProcessorAPI = new VINProcessorAPI();
